import { Button, Toaster, Tooltip, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { PanelLeft24Regular, PanelRight24Regular, PanelLeft28Regular, PanelRight28Regular } from '@fluentui/react-icons';
import { FC, PropsWithChildren, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Footer } from '../Components/Common/Footer';
import { ForceTheme } from '../Components/Common/ForceTheme';
import AppMenu from '../Components/Menu/AppMenu';
import AppMenuMobile from '../Components/Menu/AppMenuMobile';
import { useIsLandsapeOnMobile, useReponsive } from '../Hooks/useResponsive';
import { PageTurnYourDevice } from './PageTurnYourDevice';
import { ActionTypes, ConversationCreate, ConversationPatch, IGptConfiguration } from '../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { ErrorResponse, useNavigate } from 'react-router-dom';
import { performEnterpriseGPTApiCall } from '../Hooks/useMiddlewareApiCall';
import { buildRoute, ChatSpecificRoute } from '../Routes';
import { addConversationAction } from '../store/actions/conversationActions';
import { useNotification } from '../Contexts/NotificationContext';
import { useConversations } from '../store/hooks';
import { logout } from '../Security/azureAdAuthentication';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        height: '100vh',
        ...shorthands.overflow('hidden'),
        minWidth: '300px',
        position: 'relative',
    },
    sidebar: {
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: '350px',
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: tokens.shadow16,
        ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
        zIndex: 1000,
        // Smooth slide animation using transform
        ...shorthands.transition('transform', '0.4s', 'ease-in-out'),
        transform: 'translateX(0)', // Default open position
    },
    sidebarClosed: {
        transform: 'translateX(-300px)', // Slide most of it off-screen, but leave 50px visible for icons
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        ...shorthands.flex(1),
        ...shorthands.overflow('hidden', 'auto'),
        // Smooth margin animation to push content right when sidebar is open
        ...shorthands.transition('margin-left', '0.4s', 'ease-in-out'),
        marginLeft: '350px', // Default with sidebar open
    },
    contentExpanded: {
        marginLeft: '50px', // Leave space for collapsed sidebar (50px visible)
    },
    toggleButton: {
        position: 'fixed',
        top: tokens.spacingVerticalM,
        left: '70px', // Default position when sidebar is collapsed (50px + 20px margin)
        width: '40px',
        height: '40px',
        minWidth: '40px',
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        borderRadius: tokens.borderRadiusCircular,
        boxShadow: tokens.shadow4,
        color: tokens.colorNeutralForeground1,
        zIndex: 1001, // Above the sidebar
        // Smooth position animation
        ...shorthands.transition('left', '0.4s', 'ease-in-out'),
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorBrandForeground1,
            transform: 'scale(1.05)',
        },
    },
    toggleButtonOpen: {
        left: '370px', // Position when sidebar is open (350px + 20px margin)
    },
});
const useStylesMobile = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - var(--vh-offset, 0px))',
        minWidth: '300px',
        ...shorthands.overflow('hidden'),
        position: 'relative',
    },
    sidebar: {
        display: 'none', // Not used on mobile
    },
    sidebarClosed: {
        display: 'none',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        ...shorthands.flex(1),
        ...shorthands.overflow('hidden', 'auto'),
        marginLeft: '0', // No margin on mobile
    },
    contentExpanded: {
        marginLeft: '0',
    },
    toggleButton: {
        display: 'none', // Hidden on mobile since we use AppMenuMobile
    },
    toggleButtonOpen: {
        display: 'none',
    },
});

interface PageLayoutProps {
    menuDarkTheme?: boolean;
}

export const PageLayout: FC<PropsWithChildren<PageLayoutProps>> = (props) => {
    const { children, menuDarkTheme } = props;
    const { isMobile, value: classes } = useReponsive(useStyles(), useStylesMobile());
    const isMobileLandscape = useIsLandsapeOnMobile();
    const { dispatchNotification } = useNotification();
    const { dispatch } = useConversations();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(true);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    // Handle ESC key to close drawer
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMenuOpen]);

    const handleNewChat = (gpt: IGptConfiguration) => {
        performEnterpriseGPTApiCall(async (client, cancelToken) => {
            try {
                const conversationCreate = new ConversationCreate({ gpt: { id: gpt?.id, type: gpt?.type } });
                let newConversation = await client.createConversation(conversationCreate, cancelToken);
                dispatch(addConversationAction({ ...newConversation }));
                newConversation = await client.patchConversation(
                    newConversation.id,
                    new ConversationPatch({ actionType: ActionTypes.RenameConversation })
                );
                navigate(buildRoute(ChatSpecificRoute, { id: newConversation.id }));
                
                // Keep menu open - removed automatic closing for persistent sidebar
            } catch (e) {
                if ((e as ErrorResponse).status === 401) {
                    dispatchNotification('error.api.common.expiredAuthentication', 'error', 'Login', logout);
                } else if ((e as ErrorResponse).status === 426) {
                    dispatchNotification('error.api.common.invalidClientVersion', 'info', 'Update', () => window.location.reload());
                } else {
                    dispatchNotification('error.api.post.conversation', 'error');
                }
            }
        });
    };

    return isMobileLandscape ? (
        <PageTurnYourDevice />
    ) : (
        <div className={classes.root}>
            <Toaster limit={3} position='top-end' />
            
            {/* Mobile layout with AppMenuMobile */}
            {isMobile ? (
                <ForceTheme theme={menuDarkTheme ? 'dark' : undefined}>
                    <AppMenuMobile onNewChat={handleNewChat} />
                </ForceTheme>
            ) : (
                <>
                    {/* Custom sliding sidebar */}
                    <div className={mergeClasses(classes.sidebar, !isMenuOpen && classes.sidebarClosed)}>
                        <ForceTheme theme={menuDarkTheme ? 'dark' : undefined}>
                            <AppMenu onNewChat={handleNewChat} collapsed={!isMenuOpen} />
                        </ForceTheme>
                    </div>
                    
                    {/* Toggle button - positioned based on sidebar state */}
                    <Tooltip content={t('menu.toggleMenu')} relationship="label">
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={isMenuOpen ? <PanelLeft28Regular /> : <PanelRight28Regular />}
                            onClick={toggleMenu}
                            className={mergeClasses(classes.toggleButton, isMenuOpen && classes.toggleButtonOpen)}
                        />
                    </Tooltip>
                </>
            )}
            
            <div className={mergeClasses(classes.content, !isMenuOpen && classes.contentExpanded)}>
                {children}
                <Footer />
            </div>
        </div>
    );
};
